/**
 * Typed saga context helpers.
 *
 * This module provides type-safe alternatives to redux-saga's `getContext()`.
 *
 * ## Migration Guide
 *
 * **Before (untyped):**
 * ```typescript
 * function* mySaga() {
 *   const api = yield getContext('api'); // ← returns 'any'
 *   const response = yield call(api.getLocales); // ← no autocomplete
 * }
 * ```
 *
 * **After (typed):**
 * ```typescript
 * import { getTypedContext } from 'ext/lib/saga-context';
 *
 * function* mySaga() {
 *   const api = yield* getTypedContext('api'); // ← returns ApiClient!
 *   const response = yield call(api.getLocales); // ← autocomplete works!
 * }
 * ```
 *
 * ## Important Notes
 *
 * - Use `yield*` (with asterisk) instead of `yield` for proper type inference
 * - The helper returns the exact type for each context key
 * - TypeScript will show compile errors if you access non-existent context keys
 */

import { getContext } from 'redux-saga/effects';
import type { SagaContext } from './types';

/**
 * Get a typed value from saga context.
 *
 * This is a type-safe wrapper around redux-saga's `getContext()` that provides
 * full TypeScript type inference for context values.
 *
 * @param key - The context key to retrieve
 * @returns The typed context value
 *
 * @example
 * ```typescript
 * function* fetchData() {
 *   // Get typed API client
 *   const api = yield* getTypedContext('api');
 *   const data = yield call(api.getExperience, id);
 *
 *   // Get typed logger
 *   const logger = yield* getTypedContext('logger');
 *   logger.info('Data fetched successfully');
 * }
 * ```
 */
export function* getTypedContext<K extends keyof SagaContext>(
  key: K
): Generator<any, SagaContext[K], any> {
  return yield getContext(key);
}

// Re-export types for convenience
export type {
  SagaContext,
  Logger,
  CrxClient,
  Gates,
  SdkContext,
  CrxContext,
} from './types';
